import {
  ComprehensionSkillOptions,
  DIAGNOSTIC_PASSAGE_DROPDOWN_MAX,
  FluencyPassageOptions,
  FluencyPassageOptionsV2,
  getDiagnosticWordListNumber,
  isDiagnosticContentWithUnlimitedErrors,
  SessionFeedbackFormSchema,
  shouldShowDiagnosticPassageFields,
  shouldShowGradeLevelFluencyCanRetell,
} from '@/components/feedbacks/session-feedback-utils';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormCheckboxGroup } from '@/components/forms/form-checkbox-group';
import { FormRadioGroup } from '@/components/forms/form-radio-group';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { ButtonCheckbox } from '@/components/ui/button-checkbox';
import { Label } from '@/components/ui/label';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { buildBooleanSelectOptions, buildIntArray } from '@/components/ui/multi-select-utils';
import { intToString } from '@airreading/common';
import { AssessmentContent, AssessmentResult } from '@airreading/common/data';
import clsx from 'clsx';
import { Lightbulb } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';

const ProgressMonitorMaxWordsRead = 200;
const ProgressMonitorMaxIncorrectWordsCount = 30;
const ProgressMonitorMaxWordsReadFromList = 6;
const trueLabelStyle = 'bg-green-100 text-green-900 px-1 py-1';
const falseLabelStyle = 'bg-red-100 text-red-900 px-1 py-1';

function getMaxMissedErrorsByAssessmentName(assessmentName: string): number {
  const maxMissedErrorsMap: Record<string, number> = {
    'Phonics Skill 1': 4,
    'Phonics Skill 2': 3,
    'Phonics Skill 3': 3,
    'Phonics Skill 4': 5,
    'Morpheme Skill 1': 4,
    'Morpheme Skill 2': 3,
    'Morpheme Skill 3': 5,
    'Skill Preview': 5,
  };

  return maxMissedErrorsMap[assessmentName] ?? 4; // Default to 4 if not found
}

function missedWordsSelectOptions(maxMissed: number): MultiSelectOption[] {
  return Array.from({ length: maxMissed + 1 }, (_, i) => ({
    value: i.toString(),
    label: `Missed ${i}`,
    className: i === 0 ? trueLabelStyle : falseLabelStyle,
  }));
}

/** Seconds when the student finished a progress monitor read early (AIR-2086): 40–59, highest first. */
const buildProgressMonitorEarlyFinishSecondsOptions = () => {
  return [...buildIntArray(40, 59)].reverse().map((i) => i.toString());
};

const progressMonitorEarlyFinishSecondsLabel = (
  <>
    How many seconds did the student use in <strong>total</strong>?
  </>
);

const renderSecondsOption = (option: string | null | undefined) => {
  if (!option) return '';
  const seconds = parseInt(option, 10);
  return `${seconds} second${seconds > 1 ? 's' : ''}`;
};
const GradeLevelFluencyMaxWordsRead = 400;
const GradeLevelFluencyMaxNumberOfMistakes = 40;

export const EditAssessmentResultForm = ({
  result,
  form,
  assessmentIndex,
  resultIndex,
  sortedAssessmentContents,
  lastDiagnosticContentId,
  assessmentNotFinished,
  assessmentName,
  assessmentDisplayName,
  assessmentContentClass,
  isFinalListForStudent,
  studentGrade,
}: {
  result: AssessmentResult;
  form: UseFormReturn<SessionFeedbackFormSchema>;
  assessmentIndex: number;
  resultIndex: number;
  sortedAssessmentContents: AssessmentContent[];
  lastDiagnosticContentId?: string;
  assessmentNotFinished: boolean;
  assessmentName: string;
  assessmentDisplayName?: string;
  assessmentContentClass?: string;
  isFinalListForStudent?: boolean;
  studentGrade?: string | null;
}) => {
  const showGradeLevelFluencyCanRetell = shouldShowGradeLevelFluencyCanRetell(studentGrade);
  const content = sortedAssessmentContents.find((c) => c.id === result.assessmentContentId);
  if (!content) {
    return <p>Assessment content {result.assessmentContentId} not found</p>;
  }

  const { format, words, name, maxErrorsAllowed } = content;

  // Generate data-testid based on format and assessment name
  const getAssessmentTestId = (format: string, _assessmentName: string, contentName: string): string => {
    const formatMap: Record<string, string> = {
      mastery_check_phonemic_reading: 'feedback-radio-phonemic-awareness',
      mastery_check_reading: 'feedback-radio-mastery-check-reading',
      mastery_check_skill_preview: 'feedback-radio-skill-preview',
      mastery_check_reading_fluency_passage: 'feedback-radio-fluency-passage',
      mastery_check_reading_fluency_passage_v2: 'feedback-radio-fluency-passage-v2',
      mastery_check_comprehesion_passage: 'feedback-radio-comprehension-passage',
      mastery_check_comprehesion_skill: 'feedback-radio-comprehension-skill',
      diagnostic_word_list: 'feedback-radio-diagnostic-word-list',
      grade_level_fluency: 'feedback-radio-grade-level-fluency',
      progress_monitor_sounds: 'feedback-radio-progress-monitor-sounds',
      progress_monitor_words: 'feedback-radio-progress-monitor-words',
    };
    const baseId = formatMap[format] || `feedback-radio-${format.replace(/_/g, '-')}`;
    // Add content name if available for uniqueness
    const contentSuffix = contentName
      ? `-${contentName
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')}`
      : '';
    return `${baseId}${contentSuffix}`;
  };

  if (format === 'diagnostic_word_list') {
    const lastList = isFinalListForStudent ?? false;
    const isUnlimitedError = isDiagnosticContentWithUnlimitedErrors(content);
    const noErrorsMade = form.watch(
      `assessmentResults.${assessmentIndex}.results.${resultIndex}.noErrorsMadeInWordList`,
    );
    const incorrectWords = form.watch(`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWords`);
    const diagnosticListNumber = getDiagnosticWordListNumber(name);
    const showDiagnosticPassageFields = shouldShowDiagnosticPassageFields({
      contentName: name,
      missedWordCount: incorrectWords.length,
      maxErrorsAllowed,
      noErrorsMade,
    });
    // Check if this is the actual last diagnostic word list in the entire assessment
    const isLastDiagnosticWordList = lastDiagnosticContentId ? content.id === lastDiagnosticContentId : false;
    const diagnosticPassageOptions = buildIntArray(0, DIAGNOSTIC_PASSAGE_DROPDOWN_MAX).map(intToString);

    return (
      <div className="flex flex-col col-span-2">
        <FormCheckboxGroup
          options={words}
          label={`${name} - Missed word(s)`}
          form={form}
          description={
            isLastDiagnosticWordList
              ? 'This is the end of the assessment. STOP testing when finish.'
              : isUnlimitedError
                ? ''
                : `Stop testing if the student missed ${maxErrorsAllowed + 1} or more words`
          }
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWords`}
          checkboxProps={{
            disabled: !!noErrorsMade || assessmentNotFinished,
            required: !isUnlimitedError && !noErrorsMade && !assessmentNotFinished,
            className: 'data-[state=checked]:bg-red-500 data-[state=checked]:text-primary-foreground',
            secondaryLabelClassName: 'text-red-500',
          }}
          horizontal={true}
          CheckboxComponent={ButtonCheckbox}
          secondaryLabels={(_, checked) => (checked ? 'missed' : undefined)}
        />
        <FormCheckbox
          form={form}
          label={`All correct`}
          checkboxClassName="data-[state=checked]:bg-green-500 data-[state=checked]:text-primary-foreground"
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.noErrorsMadeInWordList`}
          checkboxProps={{ disabled: assessmentNotFinished, 'data-testid': 'feedback-checkbox-all-correct' }}
          CheckboxComponent={ButtonCheckbox}
        />
        {showDiagnosticPassageFields && diagnosticListNumber != null && (
          <div className="flex flex-col gap-2 mt-2">
            <Label className="font-bold">{`List ${diagnosticListNumber}: Text`}</Label>
            <div className="flex w-full justify-between gap-8">
              <FormSingleSelect
                className="flex-1"
                form={form}
                name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.totalWordsRead`}
                options={diagnosticPassageOptions}
                label="Number of words read"
                selectProps={{ disabled: assessmentNotFinished }}
              />
              <FormSingleSelect
                className="flex-1"
                form={form}
                name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWordsReadFromDiagnosticPassageCount`}
                options={diagnosticPassageOptions}
                label="Number of mistakes"
                selectProps={{ disabled: assessmentNotFinished }}
              />
            </div>
          </div>
        )}
        {lastList && (
          <Alert className="p-3 flex items-center [&>svg]:top-3 [&>svg]:left-3" variant="alert">
            <Lightbulb className="w-4 h-4 font-semibold" />
            <AlertTitle className="flex items-center m-0 px-1 font-semibold">Final list for the student</AlertTitle>
          </Alert>
        )}
      </div>
    );
  }

  if (format === 'mastery_check_reading_fluency_passage') {
    return (
      <div className="flex flex-col gap-2">
        {assessmentDisplayName && <Label>{assessmentDisplayName}</Label>}
        <FormRadioGroup
          label={name}
          form={form}
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.fluencySkillResult`}
          options={FluencyPassageOptions.map((option) => ({
            ...option,
            className: option.label === 'Could NOT read on their own' ? falseLabelStyle : trueLabelStyle,
          }))}
          radioGroupProps={{
            disabled: assessmentNotFinished,
            'data-testid': getAssessmentTestId(format, assessmentName, name),
          }}
        />
      </div>
    );
  }

  if (format === 'mastery_check_reading_fluency_passage_v2') {
    return (
      <div className="flex flex-col gap-2">
        {assessmentDisplayName && <Label>{assessmentDisplayName}</Label>}
        <FormRadioGroup
          label={name}
          form={form}
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.fluencySkillResult`}
          options={FluencyPassageOptionsV2.map((option) => ({
            ...option,
            className: option.label === 'Could NOT read on their own' ? falseLabelStyle : trueLabelStyle,
          }))}
          radioGroupProps={{
            disabled: assessmentNotFinished,
            'data-testid': getAssessmentTestId(format, assessmentName, name),
          }}
        />
      </div>
    );
  }

  if (format === 'mastery_check_reading') {
    const maxMissed = getMaxMissedErrorsByAssessmentName(assessmentName);

    return (
      <div className="flex flex-col gap-2">
        {assessmentDisplayName && <Label>{assessmentDisplayName}</Label>}
        <FormRadioGroup
          label={name}
          form={form}
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWordsCount`}
          options={missedWordsSelectOptions(maxMissed)}
          radioGroupProps={{
            disabled: assessmentNotFinished,
            'data-testid': getAssessmentTestId(format, assessmentName, name),
          }}
        />
      </div>
    );
  }

  if (format === 'mastery_check_phonemic_reading') {
    return (
      <div className="flex flex-col gap-2">
        {assessmentDisplayName && <Label>{assessmentDisplayName}</Label>}
        <FormRadioGroup
          label={name}
          form={form}
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.phonemicReadingResult`}
          options={buildBooleanSelectOptions({ trueStyle: trueLabelStyle, falseStyle: falseLabelStyle })}
          radioGroupProps={{
            disabled: assessmentNotFinished,
            'data-testid': getAssessmentTestId(format, assessmentName, name),
          }}
        />
      </div>
    );
  }

  if (format === 'mastery_check_comprehesion_passage') {
    return (
      <div className="flex flex-col gap-2">
        {assessmentDisplayName && <Label>{assessmentDisplayName}</Label>}
        <FormRadioGroup
          label={name}
          form={form}
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.comprehensionPassageResult`}
          options={buildBooleanSelectOptions({ trueStyle: trueLabelStyle, falseStyle: falseLabelStyle })}
          radioGroupProps={{
            disabled: assessmentNotFinished,
            'data-testid': getAssessmentTestId(format, assessmentName, name),
          }}
        />
      </div>
    );
  }

  if (format === 'mastery_check_comprehesion_skill') {
    return (
      <div className="flex flex-col gap-2">
        {assessmentDisplayName && <Label>{assessmentDisplayName}</Label>}
        <FormRadioGroup
          label={name}
          form={form}
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.comprehensionSkillResult`}
          options={ComprehensionSkillOptions}
          radioGroupProps={{
            disabled: assessmentNotFinished,
            'data-testid': getAssessmentTestId(format, assessmentName, name),
          }}
        />
      </div>
    );
  }

  if (format === 'mastery_check_skill_preview') {
    const maxMissed = getMaxMissedErrorsByAssessmentName(assessmentName);
    return (
      <div className="flex flex-col gap-2">
        {assessmentDisplayName && <Label>{assessmentDisplayName}</Label>}
        <FormRadioGroup
          label={name}
          form={form}
          name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWordsCount`}
          options={missedWordsSelectOptions(maxMissed)}
          radioGroupProps={{
            disabled: assessmentNotFinished,
            'data-testid': getAssessmentTestId(format, assessmentName, name),
          }}
        />
      </div>
    );
  }

  if (format === 'grade_level_fluency') {
    return (
      <div className="flex flex-col w-full">
        <div className={clsx('flex w-full justify-between gap-8', assessmentContentClass)}>
          <FormSingleSelect
            className="flex-1"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.totalWordsRead`}
            options={buildIntArray(0, GradeLevelFluencyMaxWordsRead).map((i) => i.toString())}
            label="Number of words read"
            selectProps={{ disabled: assessmentNotFinished }}
          />
          <FormSingleSelect
            className="flex-1"
            label="Number of mistakes"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWordsCount`}
            options={buildIntArray(0, GradeLevelFluencyMaxNumberOfMistakes).map(intToString)}
            selectProps={{ disabled: assessmentNotFinished }}
          />
        </div>
        {showGradeLevelFluencyCanRetell && (
          <FormRadioGroup
            label={'Is the student able to retell?'}
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.canRetell`}
            options={buildBooleanSelectOptions({ trueStyle: trueLabelStyle, falseStyle: falseLabelStyle })}
            radioGroupProps={{
              disabled: assessmentNotFinished,
              'data-testid': getAssessmentTestId(format, assessmentName, 'can-retell'),
            }}
          />
        )}
      </div>
    );
  }

  if (
    format === 'grade_level_fluency_v2' ||
    format === 'progress_monitor_sounds_v2' ||
    format === 'progress_monitor_words_v2' ||
    format === 'progress_monitor_letter_names_v2'
  ) {
    return null;
  }

  if (format === 'progress_monitor_sounds') {
    return (
      <div className="flex flex-col gap-1 w-full">
        <div className={clsx('flex flex-wrap gap-8 justify-between', assessmentContentClass)}>
          <FormSingleSelect
            label="Number of letter sounds read"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.totalWordsRead`}
            options={buildIntArray(0, ProgressMonitorMaxWordsRead).map((i) => i.toString())}
            selectProps={{ disabled: assessmentNotFinished }}
            className="flex-1"
          />
          <FormSingleSelect
            label="Number of mistakes"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWordsCount`}
            options={buildIntArray(0, ProgressMonitorMaxIncorrectWordsCount).map((i) => i.toString())}
            selectProps={{ disabled: assessmentNotFinished }}
            className="flex-1"
          />
        </div>

        <div className="w-full">
          <FormRadioGroup
            label="Number of words read incorrectly from the list"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectlWordsReadFromPMList`}
            options={missedWordsSelectOptions(ProgressMonitorMaxWordsReadFromList)}
            radioGroupProps={{
              disabled: assessmentNotFinished,
              'data-testid': getAssessmentTestId(format, assessmentName, 'words-read-incorrectly'),
            }}
            className="w-full"
          />
        </div>
      </div>
    );
  }

  if (format === 'progress_monitor_words') {
    return (
      <div className="flex flex-col gap-1 w-full">
        <div className={clsx('flex flex-wrap gap-8 justify-between', assessmentContentClass)}>
          <FormSingleSelect
            label="Number of words read"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.totalWordsRead`}
            options={buildIntArray(0, ProgressMonitorMaxWordsRead).map((i) => i.toString())}
            selectProps={{ disabled: assessmentNotFinished }}
            className="flex-1"
          />
          <FormSingleSelect
            label="Number of mistakes"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWordsCount`}
            options={buildIntArray(0, ProgressMonitorMaxIncorrectWordsCount).map((i) => i.toString())}
            selectProps={{ disabled: assessmentNotFinished }}
            className="flex-1"
          />
        </div>

        <div className={clsx('flex flex-wrap gap-8 justify-between', assessmentContentClass)}>
          <FormRadioGroup
            label="Is the student able to retell?"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.canRetell`}
            options={buildBooleanSelectOptions({ trueStyle: trueLabelStyle, falseStyle: falseLabelStyle })}
            radioGroupProps={{ disabled: assessmentNotFinished }}
            className="flex-1"
          />
          <div className="flex flex-col flex-1">
            <FormCheckbox
              label={'Click if the student finished reading early'}
              form={form}
              name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.finishedEarly`}
              checkboxProps={{ disabled: assessmentNotFinished, 'data-testid': 'feedback-checkbox-finished-early' }}
              horizontal
            />
            <FormSingleSelect
              label={progressMonitorEarlyFinishSecondsLabel}
              form={form}
              name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.totalSecondsSpent`}
              options={buildProgressMonitorEarlyFinishSecondsOptions()}
              renderOption={renderSecondsOption}
              selectProps={{
                disabled:
                  !form.watch(`assessmentResults.${assessmentIndex}.results.${resultIndex}.finishedEarly`) ||
                  assessmentNotFinished,
              }}
            />
          </div>
        </div>

        <div className="w-full">
          <FormRadioGroup
            label="Number of words read incorrectly from the list"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectlWordsReadFromPMList`}
            options={missedWordsSelectOptions(ProgressMonitorMaxWordsReadFromList)}
            radioGroupProps={{
              disabled: assessmentNotFinished,
              'data-testid': getAssessmentTestId(format, assessmentName, 'words-read-incorrectly'),
            }}
            className="w-full"
          />
        </div>
      </div>
    );
  }

  if (format === 'progress_monitor_text') {
    return (
      <div className="flex flex-col gap-1 w-full">
        <div className={clsx('flex flex-wrap gap-8 justify-between', assessmentContentClass)}>
          <FormSingleSelect
            label="Number of words read"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.totalWordsRead`}
            options={buildIntArray(0, ProgressMonitorMaxWordsRead).map((i) => i.toString())}
            selectProps={{ disabled: assessmentNotFinished }}
            className="flex-1"
          />
          <FormSingleSelect
            label="Number of mistakes"
            form={form}
            name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.incorrectWordsCount`}
            options={buildIntArray(0, ProgressMonitorMaxIncorrectWordsCount).map((i) => i.toString())}
            selectProps={{ disabled: assessmentNotFinished }}
            className="flex-1"
          />
        </div>
        <div className={clsx('flex flex-wrap gap-8 justify-between', assessmentContentClass)}>
          <div className="flex flex-col flex-1">
            <FormCheckbox
              label={'Click if the student finished reading early'}
              form={form}
              name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.finishedEarly`}
              checkboxProps={{ disabled: assessmentNotFinished, 'data-testid': 'feedback-checkbox-finished-early' }}
              horizontal
            />
            <FormSingleSelect
              label={progressMonitorEarlyFinishSecondsLabel}
              form={form}
              name={`assessmentResults.${assessmentIndex}.results.${resultIndex}.totalSecondsSpent`}
              options={buildProgressMonitorEarlyFinishSecondsOptions()}
              renderOption={renderSecondsOption}
              selectProps={{
                disabled:
                  !form.watch(`assessmentResults.${assessmentIndex}.results.${resultIndex}.finishedEarly`) ||
                  assessmentNotFinished,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
  return <div>{content.format} edit mode not implemented</div>;
};
